import { AxiosError } from 'axios';
import { Form, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { requisitesApi } from 'api';
import {
  CloseDialogHandler,
  CloseFormikDialogResult,
  FormControls,
  FormikDialog,
  FormikTextField,
} from 'components';
import { useMutation, useQueryFilters } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { QueryParams, Requisites, RequisitesFilters } from 'types';

type Values = {
  blockedMessage: string;
};

type Props = {
  open: boolean;
  count?: number;
  onClose: CloseDialogHandler;
};

export const BatchBlockRequisitesDialog: React.FC<Props> = ({
  open,
  count,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_list.batch_block_dialog',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { filtersQuery } = useQueryFilters<RequisitesFilters>();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        blockedMessage: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const initialValues: Values = useMemo(
    () => ({
      blockedMessage: '',
    }),
    [],
  );

  const mutation = useMutation<
    Requisites,
    AxiosError,
    { blockedMessage: string; params: Partial<QueryParams> }
  >(requisitesApi.batchBlock, {
    notifierType: 'execute',
    notifierMessages: {
      success: (data) => t('result', { affected: data?.affected }),
    },
  });

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      mutation.mutate(
        {
          blockedMessage: values.blockedMessage,
          params: { filters: filtersQuery },
        },
        {
          onSuccess: () => {
            formikHelpers.resetForm();
            onClose({ ok: true });
          },
        },
      );
    },
    [mutation, filtersQuery, onClose],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data.values, result.data.formikHelpers);
      } else {
        mutation.reset();
        onClose(result);
      }
    },
    [mutation, handleSubmit, onClose],
  );

  return (
    <FormikDialog
      formikProps={{
        initialValues,
        validationSchema,
      }}
      dialogProps={{
        open,
        mutation,
        title: t('title'),
        onClose: handleClose,
      }}
    >
      {() => (
        <Form>
          <div className="tw-mb-4">{`${t(
            'selected_requisites_count',
          )}: ${count}`}</div>
          <FormControls>
            <FormikTextField
              className="tw-w-full"
              label={t('message')}
              name="blockedMessage"
            />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
